import { useState } from "react";
import ReactMarkdown from "react-markdown";
import useUsers from "admin/hooks/useUsers";
import {
  getReviewStatusDisplay,
  getReviewStatusColor,
  REVIEW_STATUS_OPTIONS,
} from "admin/types/constants/review-status";
import Annotation from "admin/types/interfaces/annotation";
import type { ReviewStatus } from "admin/types/interfaces/conversation";
import { getUserInitials, getUserNameByEmail } from "admin/utils/users";
import { ChevronDown, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import remarkGfm from "remark-gfm";
import { MARKDOWN_COMPONENTS } from "~/components/assistant/lib/markdownComponents";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Textarea } from "~/components/ui/textarea";
import useToast from "~/components/ui/toast";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";

interface AnnotationDrawerProps {
  isOpen: boolean;
  annotations: Annotation[];
  isLoading: boolean;
  createAnnotation: (content: string) => Promise<Annotation | undefined>;
  updateAnnotation: (annotationId: string, content: string) => Promise<Annotation | undefined>;
  deleteAnnotation: (annotationId: string) => Promise<void>;
  reviewStatus?: ReviewStatus;
  onReviewStatusChange?: (newStatus: ReviewStatus) => Promise<void>;
}

const MAX_ANNOTATION_CHARS = 10000;

export function AnnotationDrawer({
  isOpen,
  annotations,
  isLoading,
  createAnnotation,
  updateAnnotation,
  deleteAnnotation,
  reviewStatus,
  onReviewStatusChange,
}: AnnotationDrawerProps) {
  const { showError } = useToast();
  const { data: users } = useUsers();
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  // Handle review status change
  const handleReviewStatusChange = async (newStatus: ReviewStatus) => {
    if (!onReviewStatusChange || newStatus === reviewStatus) {
      return;
    }

    setIsUpdatingStatus(true);
    try {
      await onReviewStatusChange(newStatus);
    } catch (error) {
      showError("Failed to update review status");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Helper to get user by email
  const getUserByEmail = (email: string) => users?.find((u) => u.email === email);

  // State management
  const [editingAnnotationId, setEditingAnnotationId] = useState<string | null>(null);
  // editContent: current textarea value; originalEditContent: snapshot at edit start
  // Both needed to detect changes and enable cancel/rollback without re-querying annotations
  const [editContent, setEditContent] = useState("");
  const [originalEditContent, setOriginalEditContent] = useState("");
  const [newAnnotationContent, setNewAnnotationContent] = useState("");
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Format timestamp
  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Handle edit mode
  const handleEdit = (annotation: Annotation) => {
    setEditingAnnotationId(annotation.id);
    setEditContent(annotation.content);
    setOriginalEditContent(annotation.content);
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingAnnotationId(null);
    setEditContent("");
    setOriginalEditContent("");
  };

  // Handle save edit
  const handleSaveEdit = async (annotationId: string) => {
    if (!editContent.trim() || editContent.length > MAX_ANNOTATION_CHARS) {
      return;
    }

    setIsSubmitting(true);
    try {
      await updateAnnotation(annotationId, editContent);
      setEditingAnnotationId(null);
      setEditContent("");
      setOriginalEditContent("");
    } catch (error) {
      showError("Failed to update note");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!deleteConfirmId) {
      return;
    }

    setIsSubmitting(true);
    try {
      await deleteAnnotation(deleteConfirmId);
      setDeleteConfirmId(null);
    } catch (error) {
      showError("Failed to delete note");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle create annotation
  const handleCreateAnnotation = async () => {
    if (!newAnnotationContent.trim() || newAnnotationContent.length > MAX_ANNOTATION_CHARS) {
      return;
    }

    setIsSubmitting(true);
    try {
      await createAnnotation(newAnnotationContent);
      setNewAnnotationContent("");
    } catch (error) {
      showError("Failed to create note");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if content is valid
  const isEditContentValid =
    editContent.trim().length > 0 && editContent.length <= MAX_ANNOTATION_CHARS;
  const isNewContentValid =
    newAnnotationContent.trim().length > 0 && newAnnotationContent.length <= MAX_ANNOTATION_CHARS;

  // Check if edit content changed
  const editContentChanged = editContent !== originalEditContent;

  return (
    <>
      <div
        className={`shrink-0 overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? "w-[364px] border-l border-border" : "w-0"
        }`}
      >
        <div className="w-[364px] bg-background flex flex-col h-full">
          <div className="shrink-0 border-b border-border p-4 flex items-center justify-between h-16">
            <h2 className="flex items-center gap-2 font-semibold">Notes</h2>
            <div className="flex items-center gap-2">
              {onReviewStatusChange && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="sm"
                      className={cn(
                        "h-8 gap-1 border text-xs font-medium",
                        getReviewStatusColor(reviewStatus)
                      )}
                      disabled={isUpdatingStatus}
                    >
                      <span>{getReviewStatusDisplay(reviewStatus)}</span>
                      <ChevronDown className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {REVIEW_STATUS_OPTIONS.map((option) => (
                      <DropdownMenuItem
                        key={option.value}
                        onClick={() => handleReviewStatusChange(option.value)}
                        className={reviewStatus === option.value ? "bg-accent" : ""}
                      >
                        <span
                          className={cn(
                            "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
                            option.color
                          )}
                        >
                          {option.label}
                        </span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {isLoading && (annotations?.length ?? 0) === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-muted-foreground">Loading notes...</div>
              </div>
            ) : (annotations?.length ?? 0) === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Pencil className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No notes yet</p>
              </div>
            ) : (
              [...(annotations ?? [])]
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                .map((annotation) => {
                  const isEditing = editingAnnotationId === annotation.id;
                  const user = getUserByEmail(annotation.adminUserId);

                  return (
                    <div key={annotation.id} className="border-b border-border">
                      <Card
                        variant="flat"
                        className="px-4 py-3 gap-0 group hover:bg-neutral-50 transition-colors"
                      >
                        <CardHeader className="p-0 gap-0">
                          <CardTitle className="flex items-center gap-2 text-sm font-normal">
                            <Avatar className="h-5 w-5">
                              <AvatarImage src={user?.image_url} />
                              <AvatarFallback className="text-xs">
                                {user && getUserInitials(user)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="font-semibold">
                              {getUserNameByEmail(annotation.adminUserId, users ?? [])}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {formatTimestamp(annotation.createdAt)}
                            </span>
                          </CardTitle>
                          <CardAction>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleEdit(annotation)}>
                                  <Pencil className="h-4 w-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => setDeleteConfirmId(annotation.id)}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <Trash2 className="h-4 w-4 mr-2 text-destructive" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </CardAction>
                        </CardHeader>

                        <CardContent className="p-0">
                          {isEditing ? (
                            <Textarea
                              value={editContent}
                              onChange={(e) => setEditContent(e.target.value)}
                              className="min-h-24"
                              placeholder="Edit note..."
                            />
                          ) : (
                            <div className="flex items-start gap-1">
                              <div className="text-sm prose prose-sm max-w-none text-foreground">
                                <ReactMarkdown
                                  remarkPlugins={[remarkGfm]}
                                  components={MARKDOWN_COMPONENTS}
                                >
                                  {annotation.content}
                                </ReactMarkdown>
                              </div>
                              {new Date(annotation.updatedAt).getTime() !==
                                new Date(annotation.createdAt).getTime() && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="text-xs text-muted-foreground cursor-default">
                                        (Edited)
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>{formatTimestamp(annotation.updatedAt)}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                          )}
                        </CardContent>

                        {isEditing && (
                          <CardFooter className="flex justify-end gap-2 p-0 pt-3">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleCancelEdit}
                              disabled={isSubmitting}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleSaveEdit(annotation.id)}
                              disabled={!isEditContentValid || !editContentChanged || isSubmitting}
                              loading={isSubmitting}
                            >
                              Save
                            </Button>
                          </CardFooter>
                        )}
                      </Card>
                    </div>
                  );
                })
            )}
          </div>

          <div className="border-t border-border bg-background p-4 space-y-4 shrink-0">
            <Textarea
              value={newAnnotationContent}
              onChange={(e) => setNewAnnotationContent(e.target.value)}
              placeholder="Add a note..."
              className="h-24 resize-none"
            />
            <Button
              className="w-full"
              onClick={handleCreateAnnotation}
              disabled={!isNewContentValid || isSubmitting}
              loading={isSubmitting}
            >
              Add Note
            </Button>
          </div>
        </div>
      </div>

      <AlertDialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Note?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the note.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteConfirmId(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
